package com.arenaproplay.admin.pages.analytics

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.AcqFunnelDto
import com.arenaproplay.admin.domain.admin.AdminRepository
import com.arenaproplay.admin.domain.admin.Period
import com.arenaproplay.admin.ui.components.admin.GlassCard
import com.arenaproplay.admin.ui.components.admin.SessionListSection
import com.arenaproplay.admin.ui.components.admin.VSpace
import com.arenaproplay.admin.ui.components.admin.formatNumber
import com.arenaproplay.admin.ui.components.admin.format
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodySRegular
import com.arenaproplay.admin.ui.theme.displaySmall
import com.arenaproplay.admin.ui.utils.sBorderRadius
import org.jetbrains.compose.web.css.*

// ─────────────────────────────────────────────────────────────────────────────
// Sessions tab — Funnel card + kontaq-style sessions feed
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun SessionsTabContent(
    period: Period,
    repo: AdminRepository,
    funnel: AcqFunnelDto?,
    funnelError: String?,
) {
    Column(Modifier.fillMaxWidth()) {
        // 1. Funnel card (from acquisition data, shared state from Analytics.kt)
        NaFunnelCard(funnel = funnel, error = funnelError)

        VSpace(14)

        // 2. Sessions feed — reuse existing SessionListSection (shown in BehaviorTab too)
        // The SessionListSection already has the kontaq-style anatomy with new v2 fields.
        SessionListSection(period = period, repo = repo)
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Funnel card (moved from Analytics.kt — kontaq pattern: воронка живёт на
// вкладке «Сессии», а не в «Источниках»)
// ─────────────────────────────────────────────────────────────────────────────

@Composable
internal fun NaFunnelCard(funnel: AcqFunnelDto?, error: String?) {
    GlassCard(padding = 16) {
        SpanText("Воронка: реклама → PRO", Modifier.displaySmall().color(AppColors.Ink))
        Box(Modifier.height(4.px))
        // kontaq funnelSubtitle analog
        SpanText(
            "Где мы теряем пользователей на пути к PRO",
            Modifier.styleModifier {
                property("font-size", "12px")
                property("color", "rgba(240,244,252,.50)")
            }
        )
        Box(Modifier.height(12.px))

        when {
            error != null -> SpanText("Ошибка: $error", Modifier.bodySRegular().color(AppColors.Error))
            funnel == null -> {
                Column(Modifier.fillMaxWidth().styleModifier { property("gap", "6px") }) {
                    repeat(4) { i ->
                        val ws = listOf("100%", "78%", "44%", "22%")
                        Box(
                            Modifier.height(32.px).sBorderRadius(8)
                                .styleModifier {
                                    property("width", ws[i])
                                    property("background", "rgba(255,255,255,.04)")
                                }
                        )
                    }
                }
            }
            else -> {
                val base = funnel.installs.toDouble().coerceAtLeast(1.0)
                Column(Modifier.fillMaxWidth().styleModifier { property("gap", "6px") }) {
                    NaFunnelBar(
                        label = "Активные устройства",
                        count = funnel.installs,
                        widthPct = 100.0,
                        // Primary blue gradient (Night Arena)
                        background = "linear-gradient(90deg,#0061FF,#4D8CFF)",
                        ratioLabel = null,
                    )
                    NaFunnelBar(
                        label = "Регистрация",
                        count = funnel.signups,
                        widthPct = (funnel.signups.toDouble() / base * 100).coerceIn(5.0, 100.0),
                        background = "#4D8CFF",
                        ratioLabel = "${(funnel.signups.toDouble() / base * 100).format(0)}%",
                    )
                    NaFunnelBar(
                        label = "Первое действие",
                        count = funnel.firstAction,
                        widthPct = (funnel.firstAction.toDouble() / base * 100).coerceIn(5.0, 100.0),
                        background = "#00C98F",
                        ratioLabel = null,
                    )
                    NaFunnelBar(
                        label = "Вернулся (7d)",
                        count = funnel.returned7d,
                        widthPct = (funnel.returned7d.toDouble() / base * 100).coerceIn(5.0, 100.0),
                        background = "#00E5A4",
                        ratioLabel = null,
                    )
                    NaFunnelBar(
                        label = "★ PRO",
                        count = funnel.pro,
                        widthPct = (funnel.pro.toDouble() / base * 100).coerceIn(8.0, 100.0),
                        // Yellow / PRO gold
                        background = "linear-gradient(90deg,#E4CD40,#F0DE6B)",
                        ratioLabel = null,
                        bold = true,
                    )
                }
                Box(Modifier.height(10.px))
                SpanText(
                    "ℹ Верх воронки включает тестовый трафик; нижние ступени считаются только по реальным юзерам.",
                    Modifier.styleModifier {
                        property("font-size", "11px")
                        property("color", "rgba(240,244,252,.38)")
                        property("line-height", "1.5")
                    }
                )
            }
        }
    }
}

@Composable
internal fun NaFunnelBar(
    label: String,
    count: Long,
    widthPct: Double,
    background: String,
    ratioLabel: String?,
    bold: Boolean = false,
) {
    Box(Modifier.fillMaxWidth()) {
        Box(
            Modifier.styleModifier {
                property("width", "$widthPct%")
                property("background", background)
                property("border-radius", "7px")
                property("padding", "7px 10px")
                property("display", "flex")
                property("justify-content", "space-between")
                property("align-items", "center")
                property("box-sizing", "border-box")
            }
        ) {
            SpanText(
                label,
                Modifier.styleModifier {
                    property("font-size", "11px")
                    property("font-weight", if (bold) "700" else "600")
                    property("color", "#05080F")
                }
            )
            SpanText(
                if (ratioLabel != null) "${formatNumber(count)} · $ratioLabel" else formatNumber(count),
                Modifier.styleModifier {
                    property("font-family", "'Unbounded', sans-serif")
                    property("font-variant-numeric", "tabular-nums")
                    property("font-size", "11px")
                    property("font-weight", "700")
                    property("color", "#05080F")
                }
            )
        }
    }
}
